import React, { useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Chip, Tooltip, Button } from '@mui/material';
import { MapContainer, TileLayer, Polygon, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { LatLngExpression, Map as LeafletMap } from 'leaflet';
import { AccessTime, CheckCircle, Cancel, Phone, LocationOn, Visibility } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { ServiceProvider } from '../../services/serviceProviderService';
import { Request } from '../../services/requestService';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface AutomationMapProps {
  providers: ServiceProvider[];
  requests: Request[];
  defaultCenter?: LatLngExpression;
  defaultZoom?: number;
  onRequestClick?: (request: Request) => void;
}

// Helper to normalize coordinates
const normalizeCoordinates = (coords: any): LatLngExpression[] => {
  if (!coords || !Array.isArray(coords) || coords.length === 0) return [];
  return coords.map((coord: any) => {
    if (Array.isArray(coord) && coord.length >= 2) {
      return [coord[0], coord[1]] as LatLngExpression;
    }
    return coord as LatLngExpression;
  });
};

// Get polygon center for marker placement
const getPolygonCenter = (coordinates: LatLngExpression[]): LatLngExpression => {
  if (coordinates.length === 0) return [23.5859, 58.4059]; // Default to Muscat, Oman
  const validCoords = coordinates.filter(c => Array.isArray(c) && c.length >= 2) as number[][];
  if (validCoords.length === 0) return [23.5859, 58.4059];
  
  const sum = validCoords.reduce(
    (acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]],
    [0, 0]
  );
  return [sum[0] / validCoords.length, sum[1] / validCoords.length];
};

// Get provider areas
const getProviderAreas = (provider: ServiceProvider) => {
  const attrs = provider.attributes || provider;
  if (attrs.areaPolygons?.polygons) {
    return attrs.areaPolygons.polygons;
  }
  return [];
};

// Filter in-process requests (not completed and not cancelled)
const getInProcessRequests = (requests: Request[]): Request[] => {
  return requests.filter(request => {
    const attrs = request.attributes || request;
    const status = (attrs.status || '').toLowerCase();
    const hasLocation = attrs.locationLatitude && attrs.locationLongitude;
    
    // Show requests that are not completed and not cancelled, and have location
    const isInProcess = status !== 'completed' && status !== 'cancelled';
    
    return isInProcess && hasLocation;
  });
};

// Get status color for request
const getRequestStatusColor = (status: string): string => {
  const statusColors: { [key: string]: string } = {
    pending: '#ff9800',
    assigned: '#2196f3',
    active: '#4caf50',
    invoice_creation: '#9c27b0',
    invoice_sent: '#00bcd4',
    payment_received: '#8bc34a',
  };
  return statusColors[status] || '#757575';
};

// Component to fix map size when tab becomes visible
const MapSizeFixer: React.FC<{ mapRef: React.MutableRefObject<any> }> = ({ mapRef }) => {
  const map = useMap();
  
  useEffect(() => {
    // Store map reference
    if (map) {
      mapRef.current = map;
    }
    
    // Wait for map to be fully initialized before invalidating size
    const invalidateSizeSafely = () => {
      try {
        if (map && typeof map.invalidateSize === 'function') {
          // Check if map container exists and has dimensions
          const container = map.getContainer();
          if (container && container.offsetWidth > 0 && container.offsetHeight > 0) {
            map.invalidateSize();
          }
        }
      } catch (error) {
        // Silently handle errors during initialization
        console.debug('Map size invalidation skipped:', error);
      }
    };
    
    // Multiple attempts to fix size with increasing delays
    const timers = [
      setTimeout(invalidateSizeSafely, 100),
      setTimeout(invalidateSizeSafely, 300),
      setTimeout(invalidateSizeSafely, 500),
      setTimeout(invalidateSizeSafely, 1000),
      setTimeout(invalidateSizeSafely, 2000)
    ];
    
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [map, mapRef]);
  
  // Also fix size when window is resized
  useEffect(() => {
    const handleResize = () => {
      try {
        if (map && typeof map.invalidateSize === 'function') {
          const container = map.getContainer();
          if (container && container.offsetWidth > 0 && container.offsetHeight > 0) {
            map.invalidateSize();
          }
        }
      } catch (error) {
        console.debug('Map resize handling skipped:', error);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [map]);
  
  return null;
};

// Get status label
const getRequestStatusLabel = (status: string): string => {
  const labels: { [key: string]: string } = {
    pending: 'Pending',
    assigned: 'Assigned',
    active: 'Active',
    invoice_creation: 'Invoice Creation',
    invoice_sent: 'Invoice Sent',
    payment_received: 'Payment Received',
  };
  return labels[status] || status;
};

// Create custom icon for service providers
const createProviderIcon = (isActive: boolean, providerName: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${isActive ? '#4caf50' : '#9e9e9e'};
        color: white;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 12px;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      " title="${providerName}">
        ${providerName.charAt(0).toUpperCase()}
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};

// Create custom icon for requests
const createRequestIcon = (status: string) => {
  const color = getRequestStatusColor(status);
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        color: white;
        border-radius: 50%;
        width: 25px;
        height: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 10px;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      </div>
    `,
    iconSize: [25, 25],
    iconAnchor: [12.5, 12.5],
  });
};

const AutomationMap: React.FC<AutomationMapProps> = ({
  providers,
  requests,
  defaultCenter = [23.5859, 58.4059], // Muscat, Oman
  defaultZoom = 11,
  onRequestClick,
}) => {
  const mapRef = useRef<any>(null);
  // Filter active providers
  const activeProviders = useMemo(() => {
    return providers.filter(p => {
      const attrs = p.attributes || p;
      return attrs.isActive === true;
    });
  }, [providers]);

  // Get in-process requests
  const inProcessRequests = useMemo(() => {
    return getInProcessRequests(requests);
  }, [requests]);

  // Get all unique area polygons from active providers
  const allAreas = useMemo(() => {
    const areaMap = new globalThis.Map<string, { polygon: any; providers: ServiceProvider[] }>();
    
    activeProviders.forEach(provider => {
      const areas = getProviderAreas(provider);
      
      if (areas.length > 0) {
        areas.forEach((area: any) => {
          const key = area.id || area.name;
          if (!areaMap.has(key)) {
            areaMap.set(key, { polygon: area, providers: [] });
          }
          areaMap.get(key)!.providers.push(provider);
        });
      }
    });
    
    return Array.from(areaMap.values());
  }, [activeProviders]);

  // Calculate map bounds to fit all areas and requests
  const mapBounds = useMemo(() => {
    const allCoords: LatLngExpression[] = [];
    
    allAreas.forEach(({ polygon }) => {
      const coords = normalizeCoordinates(polygon.coordinates);
      allCoords.push(...coords);
    });
    
    // Add provider locations if they have coordinates
    activeProviders.forEach(provider => {
      const attrs = provider.attributes || provider;
      if (attrs.locationLatitude && attrs.locationLongitude) {
        allCoords.push([attrs.locationLatitude, attrs.locationLongitude] as LatLngExpression);
      }
    });

    // Add request locations
    inProcessRequests.forEach(request => {
      const attrs = request.attributes || request;
      if (attrs.locationLatitude && attrs.locationLongitude) {
        allCoords.push([attrs.locationLatitude, attrs.locationLongitude] as LatLngExpression);
      }
    });
    
    if (allCoords.length === 0) return null;
    
    const lats = allCoords.map(c => (c as number[])[0]);
    const lngs = allCoords.map(c => (c as number[])[1]);
    
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)]
    ] as [[number, number], [number, number]];
  }, [allAreas, activeProviders, inProcessRequests]);

  // Fix map size when component mounts or becomes visible
  useEffect(() => {
    const invalidateSizeSafely = () => {
      try {
        if (mapRef.current) {
          const map = mapRef.current;
          const container = map?.getContainer?.();
          if (container && container.offsetWidth > 0 && container.offsetHeight > 0) {
            if (map && typeof map.invalidateSize === 'function') {
              map.invalidateSize();
            }
          }
        }
      } catch (error) {
        console.debug('Map size invalidation skipped:', error);
      }
    };
    
    // Multiple attempts with increasing delays to ensure map resizes properly
    const timers = [
      setTimeout(invalidateSizeSafely, 100),
      setTimeout(invalidateSizeSafely, 300),
      setTimeout(invalidateSizeSafely, 500),
      setTimeout(invalidateSizeSafely, 1000),
      setTimeout(invalidateSizeSafely, 2000)
    ];
    
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  return (
    <Box sx={{ 
      height: '100%', 
      width: '100%', 
      position: 'relative',
      minHeight: '500px',
      '& .leaflet-container': {
        height: '100% !important',
        width: '100% !important',
        minHeight: '500px !important',
        zIndex: 1
      }
    }}>
      <MapContainer
        key={`map-${mapBounds ? 'with-bounds' : 'default'}`}
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%', minHeight: '500px', zIndex: 1 }}
        bounds={mapBounds as any}
        boundsOptions={{ padding: [50, 50] }}
        zoomControl={true}
        scrollWheelZoom={true}
      >
        <MapSizeFixer mapRef={mapRef} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Render service areas */}
        {allAreas.map(({ polygon, providers: areaProviders }, index) => {
          const coords = normalizeCoordinates(polygon.coordinates);
          if (coords.length === 0) return null;
          
          const center = getPolygonCenter(coords);
          
          return (
            <React.Fragment key={polygon.id || `area-${index}`}>
              <Polygon
                positions={coords}
                pathOptions={{
                  color: '#3388ff',
                  fillColor: '#3388ff',
                  fillOpacity: 0.3,
                  weight: 2,
                }}
              />
              <Marker position={center}>
                <Popup>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {polygon.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {areaProviders.length} service provider{areaProviders.length !== 1 ? 's' : ''}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {areaProviders.map((provider: ServiceProvider) => {
                        const attrs = provider.attributes || provider;
                        
                        return (
                          <Box key={provider.id} sx={{ mt: 0.5, p: 0.5, bgcolor: '#f1f8f4', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircle fontSize="small" color="success" />
                              <Typography variant="body2" fontWeight="medium">
                                {attrs.name}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Popup>
              </Marker>
            </React.Fragment>
          );
        })}

        {/* Render provider markers (if they have location coordinates but no areas) */}
        {activeProviders.map((provider) => {
          const attrs = provider.attributes || provider;
          const hasAreas = getProviderAreas(provider).length > 0;
          
          // Only show marker if provider has location but no areas defined
          if (hasAreas || !attrs.locationLatitude || !attrs.locationLongitude) {
            return null;
          }
          
          return (
            <Marker
              key={`provider-${provider.id}`}
              position={[attrs.locationLatitude, attrs.locationLongitude] as LatLngExpression}
              icon={createProviderIcon(true, attrs.name || 'Provider')}
            >
              <Popup>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {attrs.name}
                  </Typography>
                  <Chip label={attrs.type} size="small" sx={{ mt: 0.5, mb: 0.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Phone fontSize="small" />
                    <Typography variant="body2">{attrs.phone}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <CheckCircle fontSize="small" color="success" />
                    <Typography variant="body2" color="success.main">
                      Active
                    </Typography>
                  </Box>
                  {attrs.area && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Area: {attrs.area}
                    </Typography>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        })}

        {/* Render in-process request markers */}
        {inProcessRequests.map((request) => {
          const attrs = request.attributes || request;
          if (!attrs.locationLatitude || !attrs.locationLongitude) return null;

          const status = attrs.status || 'pending';
          const requestNumber = attrs.requestNumber || `REQ-${request.id}`;
          
          return (
            <Marker
              key={`request-${request.id}`}
              position={[attrs.locationLatitude, attrs.locationLongitude] as LatLngExpression}
              icon={createRequestIcon(status)}
            >
              <Popup>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {requestNumber}
                  </Typography>
                  <Chip 
                    label={getRequestStatusLabel(status)} 
                    size="small" 
                    sx={{ 
                      mt: 0.5, 
                      mb: 0.5,
                      bgcolor: getRequestStatusColor(status),
                      color: 'white'
                    }} 
                  />
                  {attrs.description && (
                    <Typography variant="body2" sx={{ mt: 0.5, mb: 0.5 }}>
                      {attrs.description.length > 100 
                        ? `${attrs.description.substring(0, 100)}...` 
                        : attrs.description}
                    </Typography>
                  )}
                  {attrs.locationText && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <LocationOn fontSize="small" />
                      <Typography variant="caption" color="textSecondary">
                        {attrs.locationText}
                      </Typography>
                    </Box>
                  )}
                  {attrs.serviceProvider && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Provider: {attrs.serviceProvider?.data?.attributes?.name || attrs.serviceProvider?.attributes?.name || attrs.serviceProvider?.name || 'Not assigned'}
                      </Typography>
                    </Box>
                  )}
                  {onRequestClick && (
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      startIcon={<Visibility />}
                      onClick={() => onRequestClick(request)}
                      sx={{ mt: 1.5 }}
                    >
                      View Details
                    </Button>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          p: 2,
          zIndex: 1000,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Legend
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: '#4caf50',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            />
            <Typography variant="caption">Active Service Provider</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                bgcolor: '#3388ff',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            />
            <Typography variant="caption">Service Area</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: '#ff9800',
                borderRadius: '50%',
                border: '2px solid white',
              }}
            />
            <Typography variant="caption">In-Process Request</Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Showing {activeProviders.length} active provider{activeProviders.length !== 1 ? 's' : ''} and {inProcessRequests.length} in-process request{inProcessRequests.length !== 1 ? 's' : ''}
        </Typography>
      </Paper>
    </Box>
  );
};

export default AutomationMap;

